"use client";
import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface PasswordInputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    showStrength?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
    label,
    error,
    showStrength = false,
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const passwordStrength =
        props.value && typeof props.value === "string"
            ? props.value.length < 3
                ? "weak"
                : props.value.length < 8
                    ? "medium"
                    : "strong"
            : "";

    return (
        <div className="space-y-2">
            {label && (
                <label
                    htmlFor={props.id}
                    className="text-sm font-medium text-gray-700 block"
                >
                    {label}
                </label>
            )}
            <div className="relative">
                <input
                    {...props}
                    type={showPassword ? "text" : "password"}
                    className={`w-full px-4 py-3 pr-12 rounded-xl transition-all duration-200 text-slate-900 ${error
                        ? "border border-red-500 focus:ring-red-500"
                        : "bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        }`}
                />
                <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}

            {showStrength && props.value && typeof props.value === "string" && (
                <div className="h-1 w-full bg-gray-200 rounded mt-2 overflow-hidden">
                    <div
                        className={`h-full transition-all duration-300 ${passwordStrength === "weak"
                            ? "bg-red-500 w-1/3"
                            : passwordStrength === "medium"
                                ? "bg-yellow-500 w-2/3"
                                : "bg-green-500 w-full"
                            }`}
                    />
                </div>
            )}
        </div>
    );
};

export default PasswordInput;
